import * as React from "react";
import { useResponsiveImage } from "../hooks/useResponsiveImage.js";
import "./Img.css";

/**
 * Where an image is being rendered. Each intent maps to a `sizes` hint that
 * tells the browser which rung of the responsive ladder to pull, so a 120px
 * list icon, a ~350px card, a full-width hero, and a blurred full-screen splash
 * each request an appropriately sized variant instead of sharing one default.
 *
 * - `thumb`  — small fixed list icon (~120px).
 * - `card`   — catalog/grid poster (~350px, the historical default).
 * - `hero`   — large above-the-fold art (featured card, detail page); pulls a
 *              high rung so it stays sharp on retina.
 * - `splash` — full-screen background rendered behind a heavy blur. Capped on
 *              purpose: blur destroys detail, so we deliberately request a
 *              modest rung to keep bytes down rather than a full-res image.
 */
export type ImgIntent = "thumb" | "card" | "hero" | "splash";

const INTENT_SIZES: Record<ImgIntent, string> = {
  thumb: "120px",
  // Posters never render wider than ~310px (the 4-up catalog grid and the
  // gallery's 310px cards). Advertising 320px keeps a retina device on the
  // 640w rung (320×2) instead of jumping to 960w — roughly half the bytes for
  // a pixel-identical result — and, because every poster surface (hero ring,
  // gallery, catalog) shares this one hint, the browser fetches each cover
  // exactly once and reuses it everywhere.
  card: "(max-width: 480px) 200px, (max-width: 768px) 300px, 320px",
  hero: "(max-width: 768px) 100vw, 800px",
  splash: "640px",
};

export interface ImgProps {
  /** Source URL — typically a /game-assets/ or /game-covers/ path. */
  src: string | null | undefined;
  alt: string;
  /**
   * Intrinsic width/height for the <img> element. Setting these prevents
   * layout shift (CLS) by letting the browser reserve space before load.
   */
  width?: number;
  height?: number;
  /**
   * Rendering context that drives the default `sizes` hint. Defaults to
   * `card`. An explicit `sizes` prop always wins over the intent.
   */
  intent?: ImgIntent;
  /**
   * Responsive sizes hint (e.g. "(max-width: 600px) 200px, 400px").
   * Overrides `intent`. When omitted, the `intent` default is used.
   */
  sizes?: string;
  /**
   * When true, sets fetchpriority="high" and loading="eager" for
   * above-the-fold images (hero cards, first visible row).
   */
  priority?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Responsive <picture> component backed by the build-time derivatives manifest.
 *
 * Renders AVIF + WebP <source> elements with srcset so the browser picks the
 * best format and size. Shows an animated skeleton loader while the real image
 * loads, then cross-fades to the final image.
 *
 * Falls back to a plain <img> when no derivatives are available (the manifest
 * hasn't loaded yet or the image isn't in the manifest).
 */
export function Img({
  src,
  alt,
  width,
  height,
  intent = "card",
  sizes,
  priority = false,
  className,
  style,
}: ImgProps): React.ReactElement | null {
  const resolvedSizes = sizes ?? INTENT_SIZES[intent];
  const { avifSrcset, webpSrcset, fallbackSrc, lqip } = useResponsiveImage(src);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const imgRef = React.useRef<HTMLImageElement>(null);

  React.useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [fallbackSrc]);

  if (!src) return null;

  const loadingAttr = priority ? "eager" : "lazy";

  const wrapperStyle: React.CSSProperties = {
    position: "relative",
    overflow: "hidden",
    display: "block",
    width: "100%",
    height: "100%",
    ...style,
  };

  const imgStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
    opacity: isLoaded ? 1 : 0,
    transition: "opacity 0.3s ease",
  };

  // Prefer the build-time LQIP (a ~20px blurred thumbnail inlined in the
  // manifest) as the placeholder: it paints the card's actual colours instantly
  // and blurs up into the full image, instead of the neutral shimmer that holds
  // a blank card until the heavy file decodes. Falls back to the shimmer when
  // the manifest hasn't loaded or the image has no derivative entry.
  //
  // Require a `data:` URI before interpolating into `url("…")`: our manifest only
  // ever emits `data:image/webp;base64,…` (whose payload can't contain `"`/`)`),
  // but guarding the contract means a malformed manifest value can't silently
  // close the url() and corrupt the style.
  const hasLqip = Boolean(lqip && lqip.startsWith("data:"));
  const placeholderStyle: React.CSSProperties = hasLqip
    ? {
        opacity: isLoaded ? 0 : 1,
        backgroundImage: `url("${lqip}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        filter: "blur(12px)",
        transform: "scale(1.08)",
        animation: "none",
      }
    : { opacity: isLoaded ? 0 : 1 };

  return (
    <span className={className} style={wrapperStyle}>
      <span className="img-skeleton" aria-hidden="true" style={placeholderStyle} />
      <picture>
        {avifSrcset && <source type="image/avif" srcSet={avifSrcset} sizes={resolvedSizes} />}
        {webpSrcset && <source type="image/webp" srcSet={webpSrcset} sizes={resolvedSizes} />}
        <img
          ref={imgRef}
          src={fallbackSrc}
          alt={alt}
          width={width}
          height={height}
          loading={loadingAttr}
          decoding="async"
          {...(priority ? { fetchpriority: "high" } : {})}
          style={imgStyle}
          onLoad={() => setIsLoaded(true)}
          onError={() => setIsLoaded(true)}
        />
      </picture>
    </span>
  );
}
